import { useEffect, useMemo } from "react";
import { _Product_liquid, _Variant_liquid } from "./_types";
import { PlusIcon } from "./icons";
import { useVariantDetailsModal } from "./use-variant-details-modal";
import { useGlobalProducts } from "./global-stores";
import { getVariantDisplayTitle } from "./variant-helpers";
import { ProductGroupCheckboxImageCard } from "./product-group-checkbox-image-card";

interface Props {
  productList: _Product_liquid[];
  onProductSelect: (
    product: _Product_liquid,
    variant: _Variant_liquid,
    isChecked?: boolean
  ) => void;
  displayPrice?: boolean;
  selectedProduct?: _Product_liquid;
  selectedVariant?: _Variant_liquid;
  gridColumnsDesktop?: string;
  gridColumnsMobile?: string;
  required?: boolean;
  renderingStrategy?: "per_product" | "per_variant";
  shouldShowProductTitle?: boolean;
}

export const ProductGroupVariantSelectorImages = ({
  productList,
  displayPrice,
  selectedProduct,
  selectedVariant,
  gridColumnsDesktop,
  gridColumnsMobile,
  onProductSelect,
  required = true,
  renderingStrategy,
  shouldShowProductTitle,
}: Props) => {
  const { toggleModal, setProductData } = useVariantDetailsModal();
  const { getAsyncProduct, products } = useGlobalProducts(
    ({ hydrated, products, getAsyncProduct }) => ({ hydrated, products, getAsyncProduct })
  );

  // If this is an optional step and no product is selected, we don't want to show any selection
  const shouldShowSelection = required || (selectedProduct && selectedVariant);

  const selectedProductFromStore = useMemo(() => {
    if (selectedProduct) {
      return products[selectedProduct.handle];
    }
    return null;
  }, [selectedProduct, products]);

  const selectedImage = useMemo(() => {
    const variantFeaturedImage = (selectedVariant as any)?.featured_image;

    if (selectedProductFromStore) {
      const variant = selectedProductFromStore.variants.find((v) => v.id === selectedVariant?.id);
      const variant_product_group_selection_image =
        variant?.metafields?.custom["variant_product_group_selection_image"];

      if (variant_product_group_selection_image) {
        return {
          src: variant_product_group_selection_image,
          alt: selectedVariant?.title,
        };
      }
    }

    if (variantFeaturedImage) {
      return {
        src: variantFeaturedImage.src,
        alt: variantFeaturedImage.alt,
      };
    }

    if (selectedVariant?.featured_media) {
      return {
        src: selectedVariant?.featured_media.src,
        alt: selectedVariant?.featured_media.alt,
      };
    }

    if (selectedVariant?.image) {
      return {
        src: selectedVariant?.image,
        alt: selectedVariant?.title,
      };
    }

    if (selectedProduct?.featured_image) {
      return {
        src: selectedProduct?.featured_image,
        alt: selectedProduct?.title,
      };
    }

    return {
      src: null,
      alt: selectedProduct?.title || "",
    };
  }, [selectedVariant, selectedProduct, selectedProductFromStore]);

  useEffect(() => {
    if (selectedProduct) {
      getAsyncProduct(selectedProduct.handle);
    }
  }, [selectedProduct?.id]);

  return (
    <div>
      <div className="mb-1 flex flex-col gap-3 md:mb-8">
        <div className="relative flex h-[187px] overflow-hidden rounded-lg border-gray-300 bg-gray-100 p-4">
          {shouldShowSelection && selectedImage && selectedImage.src && (
            <img
              className="absolute bottom-0 left-0 right-0 top-0 h-full w-full object-cover"
              width={568}
              height={187}
              src={selectedImage.src}
              alt={selectedImage.alt}
            />
          )}

          {!shouldShowSelection && (
            <div className="flex h-full w-full flex-col items-center justify-center">
              <p className="text-sm text-gray-500">
                {required ? "Please select an option" : "Optional - Select an option if needed"}
              </p>
            </div>
          )}
        </div>
        {shouldShowSelection && (selectedProduct || selectedVariant) && (
          <div className="relative flex flex-1 flex-col items-end self-end">
            <button
              onClick={() => {
                if (!selectedProduct) {
                  return;
                }

                const variant = selectedProductFromStore?.variants.find(
                  (v) => v.id === selectedVariant?.id
                );
                const variant_modal_image = variant?.metafields?.custom["variant_modal_image"];
                setProductData({
                  title: `${selectedProduct?.title} ${variant?.title}`.trim(),
                  imageSrc: variant_modal_image || selectedImage?.src || "",
                  imageAlt: selectedImage?.alt || "",
                  description:
                    variant?.metafields?.custom &&
                    variant?.metafields?.custom["variant_description"]
                      ? (variant?.metafields?.custom["variant_description"] as string)
                      : "",
                  composition:
                    variant?.metafields?.custom && variant?.metafields?.custom["composition"]
                      ? (variant?.metafields?.custom["composition"] as string)
                      : "",
                  durability:
                    variant?.metafields?.custom && variant?.metafields?.custom["durability"]
                      ? (variant?.metafields?.custom["durability"] as string)
                      : "",
                  careInstructions:
                    variant?.metafields?.custom && variant?.metafields?.custom["care_instructions"]
                      ? (variant?.metafields?.custom["care_instructions"] as string)
                      : "",
                });
                toggleModal(true);
              }}
              className="flex items-center justify-start gap-[10px] rounded-[2px] rounded-tr-none border-solid border-primary-bg bg-[#DFEFFE] px-4 py-2 text-sm text-primary-bg border-w-[1px] md:justify-center"
            >
              {(() => {
                if (!selectedProduct || !selectedVariant) return null;
                const variantDisplayTitle = getVariantDisplayTitle(
                  selectedProduct,
                  selectedVariant
                );
                const displayTitle =
                  variantDisplayTitle === selectedProduct.title
                    ? selectedProduct.title
                    : `${selectedProduct.title} ${variantDisplayTitle}`.trim();
                return <span>{displayTitle}</span>;
              })()}
              <span className="flex shrink-0 items-center gap-1 border-b-[1px] border-primary-bg text-xs">
                More Info <PlusIcon className="h-2 w-2" />
              </span>
            </button>
          </div>
        )}
      </div>
      <ProductGroupCheckboxImageCard
        productList={productList}
        renderingStrategy={renderingStrategy}
        displayPrice={displayPrice}
        selectedProduct={selectedProduct}
        selectedVariant={selectedVariant}
        gridColumnsDesktop={gridColumnsDesktop}
        gridColumnsMobile={gridColumnsMobile}
        shouldShowProductTitle={shouldShowProductTitle}
        onProductSelect={onProductSelect}
      />
    </div>
  );
};
